import { CircleCheck, type LucideIcon } from "lucide-react";
import { useId, useLayoutEffect, useRef } from "react";

import { cx } from "./cx";

/**
 * One choice from a short row of them: a size, a supplier, a period (the
 * phone's Chip). Chips rather than a dropdown: a dropdown hides the options
 * behind a click, a row of chips shows every one at once.
 *
 * `tick` is for rows where several can be on (the branches somebody works
 * in): a ✓ after the label, so "on" does not rest on colour alone.
 */
export function Chip({
  label,
  selected,
  onClick,
  icon: Icon,
  tick,
  disabled,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
  icon?: LucideIcon;
  tick?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      role={tick ? "checkbox" : "radio"}
      aria-checked={selected}
      className={cx(
        "inline-flex min-h-10 max-w-full shrink-0 items-center gap-1 rounded-full border px-4 py-1.5 text-left text-label leading-snug font-semibold transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-60",
        selected
          ? "border-primary bg-primary-soft text-primary"
          : "border-border bg-surface text-body hover:border-primary hover:text-primary",
      )}
    >
      {Icon && <Icon size={16} aria-hidden="true" />}
      <span>{label}</span>
      {tick && selected && <CircleCheck size={16} aria-hidden="true" />}
    </button>
  );
}

/**
 * A row of single-choice chips, with a name for screen readers, that works as
 * any group of radio buttons does: Tab stops once, on the chosen chip (or the
 * first), and the arrow keys move between them.
 */
export function ChipGroup({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  const group = useRef<HTMLDivElement>(null);

  // After every draw: chips come and go, and the chosen one changes.
  useLayoutEffect(() => {
    const chips = Array.from(group.current?.querySelectorAll<HTMLButtonElement>("button") ?? []);
    const chosen = chips.find((c) => c.getAttribute("aria-checked") === "true" && !c.disabled);
    const stop = chosen ?? chips.find((c) => !c.disabled);
    for (const chip of chips) chip.tabIndex = chip === stop ? 0 : -1;
  });

  return (
    <div
      ref={group}
      role="radiogroup"
      aria-label={label}
      className={cx("flex flex-wrap gap-2", className)}
      onKeyDown={(e) => {
        if (!["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp"].includes(e.key)) return;
        const chips = Array.from(e.currentTarget.querySelectorAll<HTMLButtonElement>("button:not(:disabled)"));
        const at = chips.indexOf(document.activeElement as HTMLButtonElement);
        if (at === -1) return;
        e.preventDefault();
        const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : -1;
        const next = chips[(at + step + chips.length) % chips.length];
        if (!next) return;
        for (const chip of chips) chip.tabIndex = chip === next ? 0 : -1;
        next.focus();
      }}
    >
      {children}
    </div>
  );
}

/** A yes/no with its label beside it; the whole row is clickable (the phone's Toggle). */
export function Toggle({
  label,
  hint,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}) {
  const id = useId();
  return (
    <label
      htmlFor={id}
      className={cx(
        "flex min-h-12 items-center gap-4 py-1",
        disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
      )}
    >
      <span className="flex flex-1 flex-col gap-0.5">
        <span className="text-base text-text">{label}</span>
        {hint && <span className="text-small text-muted">{hint}</span>}
      </span>
      <input
        id={id}
        type="checkbox"
        role="switch"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        className={cx(
          "relative h-7 w-12 shrink-0 rounded-full transition-colors",
          "peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary",
          checked ? "bg-primary" : "bg-border",
        )}
      >
        <span
          className={cx(
            "absolute top-1 left-1 size-5 rounded-full bg-surface shadow-card transition-transform",
            checked && "translate-x-5",
          )}
        />
      </span>
    </label>
  );
}
